import { Link } from "react-router-dom";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Bot, Sparkles, ShieldCheck, Activity, Boxes, Layers, GitBranch, ChevronRight, Container, Truck, Anchor, Plane,
} from "lucide-react";

const AGENTS = [
  { name: "Prospect Agent", desc: "Surfaces and ranks logistics-fit accounts by buying signals.", icon: GitBranch },
  { name: "Research Agent", desc: "Builds 60-second briefs on companies & decision makers.", icon: Layers },
  { name: "Personalization Agent", desc: "Writes one-of-one outreach grounded in signal context.", icon: Sparkles },
  { name: "Safety Agent", desc: "Guards LinkedIn accounts with adaptive throttling.", icon: ShieldCheck },
  { name: "Content Agent", desc: "Generates LinkedIn posts & thought leadership.", icon: Bot, soon: true },
  { name: "Engagement Agent", desc: "Picks the next best action for every prospect.", icon: Activity, soon: true },
  { name: "CRM Agent", desc: "Mirrors activity into HubSpot / Salesforce / Pipedrive.", icon: Boxes, soon: true },
  { name: "Analytics Agent", desc: "Optimizes campaigns from outcomes, not vanity metrics.", icon: Container, soon: true },
];

const VERTICALS = [
  { icon: Container, label: "Freight Forwarding" },
  { icon: Truck, label: "Trucking & Last Mile" },
  { icon: Anchor, label: "Ports & Drayage" },
  { icon: Plane, label: "Air & Ocean Cargo" },
  { icon: Boxes, label: "Warehousing & 3PL" },
];

const PLANS = [
  { id: "starter", name: "Starter", price: 49, tagline: "Solo operators starting outbound",
    features: ["1 workspace", "500 prospects", "2 LinkedIn accounts", "AI personalization"] },
  { id: "team", name: "Team", price: 199, tagline: "Growth-stage logistics & SaaS teams", featured: true,
    features: ["3 workspaces", "5,000 prospects", "10 LinkedIn accounts", "All 4 AI agents", "Inbox & analytics"] },
  { id: "agency", name: "Agency", price: 499, tagline: "Agencies running multi-client outbound",
    features: ["Unlimited workspaces", "25,000 prospects", "50 LinkedIn accounts", "Multi-tenant agency mode", "Priority AI runs"] },
  { id: "enterprise", name: "Enterprise", price: 1499, tagline: "Custom AI capacity & SLAs",
    features: ["Everything in Agency", "Unlimited prospects", "Dedicated AI capacity", "SSO + custom RBAC", "Logistics intel SLA"] },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* NAV */}
      <header className="glass sticky top-0 z-40 px-6 lg:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
          <div className="w-7 h-7 rounded-sm bg-foreground text-background flex items-center justify-center font-black tracking-tighter">L</div>
          <span className="font-display font-semibold tracking-tightest">LogiGrowth<span className="text-signal-500">.</span>AI</span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
          <a href="#agents" className="hover:text-foreground transition-colors" data-testid="nav-link-agents">AI Agents</a>
          <a href="#vertical" className="hover:text-foreground transition-colors" data-testid="nav-link-vertical">Logistics Intel</a>
          <a href="#pricing" className="hover:text-foreground transition-colors" data-testid="nav-link-pricing">Pricing</a>
        </nav>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link to="/login" data-testid="landing-login-link" className="hidden md:inline-flex text-sm px-3 py-2 rounded-sm hover:bg-muted/60 transition-colors">Sign in</Link>
          <Link to="/signup" data-testid="landing-cta-signup">
            <Button className="rounded-sm">Start free trial<ArrowRight className="w-4 h-4 ml-1.5" /></Button>
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative px-6 lg:px-12 pt-16 lg:pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg-dot opacity-40 pointer-events-none" />
        <div className="relative max-w-6xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm border border-border bg-card/60 text-xs font-mono tracking-wider uppercase mb-8 animate-fade-in">
            <span className="signal-dot" />
            <span className="text-muted-foreground">Built for logistics &amp; supply chain GTM</span>
          </div>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-7xl tracking-tightest leading-[0.95] max-w-4xl animate-fade-in">
            The AI Revenue<br />
            Operating System<br />
            for <span className="font-black">B2B logistics.</span>
          </h1>
          <p className="mt-7 text-base lg:text-lg text-muted-foreground max-w-2xl leading-relaxed animate-fade-in">
            LogiGrowth deploys a team of autonomous AI agents that find logistics prospects,
            research accounts, personalize outreach, and book meetings — safely, at scale.
            Not another LinkedIn bot. A revenue OS.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3 animate-fade-in">
            <Link to="/signup" data-testid="hero-cta-primary">
              <Button size="lg" className="rounded-sm text-base px-6 py-6">Launch your AI revenue team<ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
            <a href="#agents" data-testid="hero-cta-secondary"
              className="text-sm px-5 py-3 rounded-sm border border-border hover:border-foreground/30 transition-colors inline-flex items-center gap-2">
              Meet the 8 agents <ChevronRight className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* KPI strip */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 grid-borders bg-card/30 max-w-4xl">
            {[
              { k: "+412%", l: "Avg pipeline uplift" },
              { k: "12.4%", l: "Reply rate (60d)" },
              { k: "8", l: "Autonomous agents" },
              { k: "0", l: "LinkedIn restrictions" },
            ].map((s, i) => (
              <div key={i} className="p-6">
                <div className="text-3xl font-display font-semibold tracking-tightest" data-testid={`hero-stat-${i}`}>{s.k}</div>
                <div className="mt-2 label-eyebrow">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VERTICAL */}
      <section id="vertical" className="px-6 lg:px-12 py-20 border-t border-border">
        <div className="max-w-6xl">
          <div className="label-eyebrow mb-4">Vertical Specialization</div>
          <h2 className="font-display font-light text-3xl lg:text-5xl tracking-tightest max-w-3xl">
            Built around the signals that move <span className="font-black">freight, customs &amp; supply chains.</span>
          </h2>
          <p className="mt-5 text-muted-foreground max-w-2xl">
            Generic SDR tools don't see fleet expansion, new trade lanes, customs spikes or warehouse openings.
            LogiGrowth does — and turns those signals into outreach the same day.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 grid-borders">
            {VERTICALS.map(({ icon: Icon, label }, i) => (
              <div key={label} className="p-6 flex flex-col gap-3" data-testid={`vertical-${i}`}>
                <Icon className="w-5 h-5 text-signal-500" strokeWidth={1.5} />
                <span className="text-sm font-medium">{label}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-2">
            {["fleet expansion","warehouse opening","new trade lane","hiring ops manager","customs spike","supply chain scaling","ecommerce growth","cross-border expansion"].map(s => (
              <span key={s} className="signal-pill">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* AGENTS */}
      <section id="agents" className="px-6 lg:px-12 py-20 border-t border-border bg-muted/20">
        <div className="max-w-6xl">
          <div className="label-eyebrow mb-4">The Agent Layer</div>
          <h2 className="font-display font-light text-3xl lg:text-5xl tracking-tightest max-w-3xl">
            Eight agents that <span className="font-black">run your GTM</span> while you sleep.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 grid-borders">
            {AGENTS.map(({ name, desc, icon: Icon, soon }, i) => (
              <div key={name} className="p-6 group hover:bg-card/60 transition-colors" data-testid={`agent-card-${i}`}>
                <div className="flex items-center justify-between mb-5">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  {soon ? <span className="text-[0.6rem] tracking-widest uppercase text-amber-accent">Phase 2</span>
                        : <span className="text-[0.6rem] tracking-widest uppercase text-signal-500">Live</span>}
                </div>
                <div className="text-base font-display font-semibold tracking-tight">{name}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="px-6 lg:px-12 py-20 border-t border-border">
        <div className="max-w-6xl">
          <div className="label-eyebrow mb-4">Pricing</div>
          <h2 className="font-display font-light text-3xl lg:text-5xl tracking-tightest max-w-3xl">
            Built for solo operators to <span className="font-black">multi-client agencies.</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-0 grid-borders">
            {PLANS.map((p) => (
              <div key={p.id} data-testid={`pricing-card-${p.id}`}
                className={`p-7 flex flex-col ${p.featured ? "bg-card relative" : ""}`}>
                {p.featured && (
                  <div className="absolute -top-px left-0 right-0 h-0.5 bg-signal-500" />
                )}
                <div className="label-eyebrow">{p.name}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-display font-semibold tracking-tightest">${p.price}</span>
                  <span className="text-xs text-muted-foreground">/ mo</span>
                </div>
                <p className="mt-3 text-sm text-muted-foreground min-h-[2.5rem]">{p.tagline}</p>
                <ul className="mt-6 space-y-2 text-sm flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <ChevronRight className="w-3.5 h-3.5 mt-1 text-signal-500 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="mt-7" data-testid={`pricing-cta-${p.id}`}>
                  <Button className={`w-full rounded-sm ${p.featured ? "" : "bg-foreground/90"}`}>Start trial</Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="px-6 lg:px-12 py-24 border-t border-border bg-foreground text-background">
        <div className="max-w-4xl">
          <h2 className="font-display font-light text-3xl lg:text-5xl tracking-tightest">
            Stop hiring SDRs.<br />
            <span className="font-black">Deploy your AI revenue team.</span>
          </h2>
          <div className="mt-9">
            <Link to="/signup" data-testid="footer-cta">
              <Button size="lg" className="bg-signal-500 hover:bg-signal-400 text-white rounded-sm px-7 py-6 text-base">
                Start free trial <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="px-6 lg:px-12 py-7 border-t border-border text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-4">
        <span>© {new Date().getFullYear()} LogiGrowth AI. Logistics-native revenue infrastructure.</span>
        <span className="font-mono tracking-wider">v0.1.0 · MVP</span>
      </footer>
    </div>
  );
}
